/**
 * Plan complexe, mode « z ↦ az + b » : a = r e^{iθ} se règle (module et
 * argument), b se déplace sur la figure ; un drapeau et son image. Selon a et
 * b : translation, homothétie, rotation, ou les deux à la fois (similitude
 * directe, nommée en prépa), de centre le point fixe ω = b / (1 − a).
 */

import { useRef, useState } from 'react';
import { Note, SECONDARY_BUTTON, Stepper } from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { snapPoint } from '../../ui/frame';
import { DragHandle, HaloText, INK, VectorArrow } from '../../ui/svg-parts';
import { useDrag } from '../../ui/use-drag';
import { radianLabel } from '../trig-circle/trig-values';
import { ARG_STEPS, affineMap, applyMap, numTex, zPlain, zTex, type Z } from './complex';
import {
  Box,
  Formula,
  Plane,
  PointMark,
  RES_COLOR,
  SOFT,
  VP,
  Z2_COLOR,
  arcPath,
  frame,
  inView,
  px,
} from './parts';

/** le drapeau : mât de (1 ; 0) à (1 ; 2), fanion jusqu'à (2 ; 1,5) */
const FLAG: readonly Z[] = [
  { a: 1, b: 0 },
  { a: 1, b: 2 },
  { a: 2, b: 1.5 },
  { a: 1, b: 1 },
];
/** le point suivi (bout du fanion) */
const TIP = FLAG[2]!;

const path = (pts: readonly Z[]) =>
  pts
    .map((z, i) => `${i ? 'L' : 'M'} ${frame.tx(z.a).toFixed(2)} ${frame.ty(z.b).toFixed(2)}`)
    .join(' ');

const PRESETS = [
  { label: 'Translation', r: 1, k: 0, b: { a: 2, b: -1 } },
  { label: 'Homothétie', r: 2, k: 0, b: { a: 0, b: 0 } },
  { label: 'Rotation', r: 1, k: 6, b: { a: 0, b: 0 } },
] as const;

export function MapMode({ rank }: { rank: number }) {
  const [r, setR] = useState(1);
  const [ki, setKi] = useState(ARG_STEPS.indexOf(6));
  const [b, setB] = useState<Z>({ a: 0, b: 0 });
  const svgRef = useRef<SVGSVGElement>(null);
  const k15 = ARG_STEPS[ki]!;
  const info = affineMap(r, k15, b, rank);
  const image = FLAG.map((z) => applyMap(info.a, b, z));
  const tipImg = applyMap(info.a, b, TIP);
  const drag = useDrag<'b'>({
    svgRef,
    frame,
    ids: ['b'],
    pointOf: () => ({ x: b.a, y: b.b }),
    onDrag: (_, p) => {
      const q = snapPoint(p, VP, 0.5, 0.5);
      setB({ a: q.x, b: q.y });
    },
    keyStep: 0.5,
  });
  const B = px(b);
  const O = px({ a: 0, b: 0 });
  const C = info.center ? px(info.center) : null;
  const showAngle =
    info.center &&
    inView(info.center) &&
    Math.hypot(TIP.a - info.center.a, TIP.b - info.center.b) > 0.4;
  const ang = (from: Z) => Math.atan2(from.b - info.center!.b, from.a - info.center!.a);
  const outImage = image.some((z) => !inView(z));
  const fTex = `f(z) = ${info.kind === 'translation' || info.kind === 'identity' ? 'z' : 'az'}${
    b.a === 0 && b.b === 0 ? '' : ' + b'
  }`;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {PRESETS.map((p) => (
          <button
            key={p.label}
            type="button"
            className={SECONDARY_BUTTON}
            onClick={() => {
              setR(p.r);
              setKi(ARG_STEPS.indexOf(p.k));
              setB(p.b);
            }}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Stepper
          label="|a|"
          value={r}
          min={0.5}
          max={3}
          step={0.5}
          format={(v) => numTex(v).replace('{,}', ',')}
          onChange={setR}
        />
        <Stepper
          label="arg a"
          value={ki}
          min={0}
          max={ARG_STEPS.length - 1}
          format={(i) => radianLabel(ARG_STEPS[i]!).plain}
          onChange={setKi}
        />
      </div>

      <Plane
        svgRef={svgRef}
        label={`Le drapeau et son image par z ↦ az + b, b = ${zPlain(b)}`}
        svgProps={drag.svgProps}
      >
        {info.kind === 'translation' && (
          <>
            <VectorArrow
              x1={O.x}
              y1={O.y}
              x2={B.x}
              y2={B.y}
              color={Z2_COLOR}
              width={2.5}
              inset={8}
            />
            <VectorArrow
              x1={frame.tx(TIP.a)}
              y1={frame.ty(TIP.b)}
              x2={frame.tx(tipImg.a)}
              y2={frame.ty(tipImg.b)}
              color={SOFT}
              width={1.5}
              dashed
            />
          </>
        )}
        {showAngle && C && (
          <g aria-hidden>
            <line
              x1={C.x}
              y1={C.y}
              x2={frame.tx(TIP.a)}
              y2={frame.ty(TIP.b)}
              stroke={SOFT}
              strokeDasharray="4 4"
            />
            <line
              x1={C.x}
              y1={C.y}
              x2={frame.tx(tipImg.a)}
              y2={frame.ty(tipImg.b)}
              stroke={RES_COLOR}
              strokeDasharray="4 4"
            />
            <path
              d={arcPath(C.x, C.y, 22, ang(TIP), ang(tipImg))}
              fill="none"
              stroke={RES_COLOR}
              strokeWidth={2}
            />
          </g>
        )}
        <path
          d={path(FLAG)}
          fill="none"
          stroke={INK}
          strokeWidth={3}
          strokeLinejoin="round"
          aria-hidden
        />
        <path
          d={path(image)}
          fill="none"
          stroke={RES_COLOR}
          strokeWidth={3}
          strokeLinejoin="round"
          aria-hidden
          data-testid="cp-image"
        />
        <circle cx={frame.tx(TIP.a)} cy={frame.ty(TIP.b)} r={4} fill={INK} aria-hidden />
        {inView(tipImg) && (
          <circle
            cx={frame.tx(tipImg.a)}
            cy={frame.ty(tipImg.b)}
            r={4}
            fill={RES_COLOR}
            aria-hidden
          />
        )}
        {/* centre O : déjà nommé, pas de second nom au même endroit */}
        {info.center && (info.center.a !== 0 || info.center.b !== 0) && (
          <PointMark z={info.center} color={RES_COLOR} label="Ω" testId="cp-center" />
        )}
        <HaloText x={O.x - 13} y={O.y + 16} fontSize={14} fontWeight={700} fill={INK} aria-hidden>
          O
        </HaloText>
        <DragHandle
          x={B.x}
          y={B.y}
          color={Z2_COLOR}
          label="b"
          labelDx={15}
          labelDy={15}
          name={`Point d’affixe b = ${zPlain(b)}`}
          active={drag.active === 'b'}
          keyProps={drag.keyProps('b')}
        />
      </Plane>

      <div className="space-y-2">
        <Box title="La transformation" testId="cp-map">
          <Formula tex={fTex} />
          <Formula tex={info.aTex} />
          <Formula tex={`b = ${zTex(b)}`} />
          {info.centerTex && <Formula tex={info.centerTex} />}
        </Box>
        <Note tone="success">{frenchSpaces(info.say)}</Note>
        {outImage && (
          <Note>
            {frenchSpaces(
              'Une partie de l’image sort de la figure : réduis |a| ou rapproche b de O.',
            )}
          </Note>
        )}
      </div>
    </div>
  );
}
